/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team <mailto:radmanesh@gmail.com Arman Radmanesh> <mailto:salarirad@gmail.com Salar Rad>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from "firebase/functions";
import {
  collection, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { useAlert } from '../../context/AlertContext';
import ParticipantsGrid from './participants/ParticipantsGrid';
import PropTypes from 'prop-types';

function ParticipantsPanel(props) {
  const { project, isLoading, setIsLoading } = props;
  const [participants, setParticipants] = useState([]);
  // participantId -> that participant's most recent fetch job (live).
  const [lastJobs, setLastJobs] = useState({});
  const navigate = useNavigate();
  const { addAlert } = useAlert();

  /**
   * Fetches participants data from the server
   */
  const fetchParticipants = useCallback(async () => {
    setIsLoading(true);
    try {
      if (project?.id) {
        const getProjectParticipants = httpsCallable(functions, "getProjectParticipants");
        const result = await getProjectParticipants({ projectId: project.id });
        setParticipants(result.data?.participants || []);
      }
    } catch (err) {
      console.error("Failed to fetch participants", err);
      addAlert("error", "Failed to fetch participants");
    } finally {
      setIsLoading(false);
    }
  }, [project?.id, setIsLoading, addAlert]);

  // Initial fetch
  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // Latest fetch per participant, from the project's recent jobs (same query and index as the
  // Google Health tab). Jobs are newest first, so the first one seen per participant wins.
  useEffect(() => {
    if (!project?.id) return undefined;
    return onSnapshot(
      query(collection(db, 'healthFetchJobs'), where('projectId', '==', project.id),
        orderBy('createdAt', 'desc'), limit(50)),
      (snap) => {
        const map = {};
        for (const d of snap.docs) {
          const j = { id: d.id, ...d.data() };
          for (const pid of j.participantIds || []) if (!map[pid]) map[pid] = j;
        }
        setLastJobs(map);
      },
      (err) => console.error('healthFetchJobs query failed', err),
    );
  }, [project?.id]);

  return (
    <ParticipantsGrid
      participants={participants}
      lastJobs={lastJobs}
      onInfoClick={(id) => navigate(`/admin/participants/${id}/project/${project?.id}`)}
      onRefresh={fetchParticipants}
      isLoading={isLoading}
    />
  );
}

ParticipantsPanel.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string
  }),
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired
};

export default ParticipantsPanel;